import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DescriptionIcon from '@mui/icons-material/Description';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import approvalDelegationIcon from '../../assets/landing/img/approval_delegation_60.svg';
import { landingTheme } from './theme';

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const features: Feature[] = [
  {
    icon: (
      <Box
        component="img"
        src={approvalDelegationIcon}
        alt="Approval Delegation"
        sx={{ width: 60, height: 60 }}
      />
    ),
    title: 'Free to Use',
    description:
      'Grant Guardian is completely free for U.S. based foundations and philanthropic organizations.',
  },
  {
    icon: <AutoAwesomeIcon sx={{ fontSize: 60 }} />,
    title: 'Advanced AI Analyses',
    description:
      "Grant Guardian harnesses AI to extract numerical values from documents such as 990s, balance sheets, and income statements (both audited and unaudited) and inputs them into customizable ratios that together provide a targeted view of a nonprofit's financial position.",
  },
  {
    icon: <TuneIcon sx={{ fontSize: 60 }} />,
    title: 'Customized Reports',
    description:
      "Grant Guardian's financial reports are fully customizable, allowing you to tailor metrics, thresholds, and weights specifically to your organization's funding priorities and grantmaking philosophy. This flexibility enables users to present clear, relevant data to stakeholders while maintaining a transparent audit trail for compliance.",
  },
  {
    icon: <DescriptionIcon sx={{ fontSize: 60 }} />,
    title: 'Automated Data Extraction',
    description:
      'As an enterprise-grade solution, Grant Guardian automates data extraction from financial reports, standardizes assessments, and minimizes user error and bias.',
  },
];

export default function Features() {
  return (
    <Box id="features" sx={{ py: 10, backgroundColor: 'grey.50' }}>
      <Container maxWidth="xl">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '3rem', md: '4rem' },
              fontWeight: 700,
              mb: 2,
              color: landingTheme.colors.primary,
            }}
          >
            Key Features
          </Typography>
          <Box
            sx={{
              width: 80,
              height: 3,
              background: landingTheme.gradients.border,
              mx: 'auto',
              mb: 3,
            }}
          />
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: '700px', mx: 'auto' }}
          >
            Grant Guardian leverages advanced AI to transform complex financial
            documents into clear, actionable insights, enabling users to assess
            a nonprofit&#39;s financial position with confidence and efficiency,
            and freeing up time for meaningful engagement with grantees.
          </Typography>
        </Box>

        {/* Features Grid */}
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid
              size={{ xs: 12, sm: 6, md: 3 }}
              key={index}
              sx={{ display: 'flex' }}
            >
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                style={{ width: '100%', display: 'flex' }}
              >
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: 3,
                    transition: 'all 0.3s ease',
                    borderRadius: '16px',
                    border: `2px solid ${landingTheme.colors.lightGray}`,
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: `0 10px 30px rgba(0, 125, 153, 0.15)`,
                      borderColor: landingTheme.colors.primary,
                    },
                  }}
                >
                  <Box
                    sx={{
                      color: landingTheme.colors.primary,
                      mb: 2,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <CardContent>
                    <Typography
                      variant="h6"
                      color={landingTheme.colors.black}
                      sx={{ fontWeight: 600, mb: 2 }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
